import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Skill } from '../../types';

interface SkillSelectorProps {
  selectedSkillIds: number[];
  onChange: (skillIds: number[]) => void;
  options: Skill[];
  placeholder?: string;
  maxSkills?: number;
  className?: string;
  categories?: Record<string, string[]>; // Map of Category Name -> Array of Skill Names
}

const SkillSelector: React.FC<SkillSelectorProps> = ({ 
  selectedSkillIds, 
  onChange, 
  options,
  maxSkills = 10,
  placeholder = "Select specializations...",
  className = "",
  categories
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleSkillClick = (skillId: number) => {
    if (selectedSkillIds.includes(skillId)) {
      onChange(selectedSkillIds.filter(id => id !== skillId));
    } else {
      if (selectedSkillIds.length < maxSkills) {
        onChange([...selectedSkillIds, skillId]);
      }
    }
    setSearchTerm(''); 
  };

  const removeSkill = (skillIdToRemove: number) => {
    onChange(selectedSkillIds.filter(id => id !== skillIdToRemove));
  };

  // Filter options based on search term
  const filteredOptions = options.filter(skill => 
    skill.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group options if categories are provided or if skills have category field
  const groupedOptions = React.useMemo(() => {
    if (searchTerm) return null; // Don't group when searching

    const groups: Record<string, Skill[]> = {};
    const categorizedSkillIds = new Set<number>();

    // Initialize groups from props if available
    if (categories) {
      Object.keys(categories).forEach(cat => {
        groups[cat] = [];
      });
    }

    // Assign skills to groups
    options.forEach(skill => {
      // Priority 1: Use skill's own category field (from DB)
      if (skill.category) {
        if (!groups[skill.category]) {
          groups[skill.category] = [];
        }
        groups[skill.category].push(skill);
        categorizedSkillIds.add(skill.id);
        return;
      }

      // Priority 2: Use categories prop (legacy/fallback)
      if (categories) {
        let foundCategory = false;
        for (const [category, skillNames] of Object.entries(categories)) {
          if (skillNames.includes(skill.name)) {
            if (!groups[category]) groups[category] = [];
            groups[category].push(skill);
            categorizedSkillIds.add(skill.id);
            foundCategory = true;
            break; // Assign to first matching category
          }
        }
        if (foundCategory) return;
      }

      // Fallback: Other
      if (!groups['Other']) groups['Other'] = [];
      groups['Other'].push(skill);
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  }, [options, categories, searchTerm]);

  // Get selected skill objects for display
  const selectedSkills = options.filter(skill => selectedSkillIds.includes(skill.id));
  
  return (
    <div className={`w-full ${className}`} ref={dropdownRef}>
      {/* Selected Skills Display (Chips) */}
      <div className="flex flex-wrap gap-2 mb-3">
        {selectedSkills.map(skill => (
          <span 
            key={skill.id} 
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-black dark:bg-white text-white dark:text-black"
          >
            {skill.name}
            <button
              type="button"
              onClick={() => removeSkill(skill.id)}
              className="ml-1.5 h-3.5 w-3.5 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
        {selectedSkillIds.length === 0 && (
            <span className="text-sm text-gray-400 dark:text-gray-500 italic">No specializations selected</span>
        )}
      </div>

      {/* Search/Dropdown Trigger */}
      <div className="relative">
        <div className="relative">
            <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder={placeholder}
            className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-black/5 dark:focus:ring-white/5 focus:border-black dark:focus:border-white transition-all text-sm text-gray-900 dark:text-white"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                {showDropdown ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
            </div>
        </div>

        {/* Dropdown Content */}
        {showDropdown && (
          <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-800 max-h-60 overflow-y-auto custom-scrollbar">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">No matching specializations found</div>
            ) : (
              <div className="p-2">
                {groupedOptions ? (
                  // Render Grouped Options
                  Object.entries(groupedOptions).map(([category, skills]) => (
                    <div key={category} className="mb-3 last:mb-0">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
                        {category}
                      </h4>
                      <div className="flex flex-wrap gap-1.5">
                        {skills.map(skill => (
                          <button
                            key={skill.id}
                            type="button"
                            onClick={() => handleSkillClick(skill.id)}
                            disabled={selectedSkillIds.includes(skill.id)}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                              selectedSkillIds.includes(skill.id)
                                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white'
                            }`}
                          >
                            {skill.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  // Render Flat List (Search results or no categories)
                  <div className="flex flex-wrap gap-1.5">
                    {filteredOptions.map(skill => (
                      <button
                        key={skill.id}
                        type="button"
                        onClick={() => handleSkillClick(skill.id)}
                        disabled={selectedSkillIds.includes(skill.id)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                          selectedSkillIds.includes(skill.id)
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white'
                        }`}
                      >
                        {skill.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="mt-2 flex justify-between items-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
            {selectedSkillIds.length}/{maxSkills} selected
        </p>
        {showDropdown && (
            <button 
                type="button" 
                onClick={() => setShowDropdown(false)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
                Done
            </button>
        )}
      </div>
    </div>
  );
};

export default SkillSelector;
